import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AnimalesPage from "./pages/AnimalesPage";
import AnimalDetailPage from "./pages/AnimalDetailPage";
import SesionesPage from "./pages/SesionesPage";
import SesionesTrabajoPage from "./pages/SesionesTrabajoPage";
import DashboardPage from "./pages/DashboardPage";
import ProyeccionesPage from "./pages/ProyeccionesPage";
import AlertasPage from "./pages/AlertasPage";
import AdminLicenciasPage from "./pages/AdminLicenciasPage";
import ReproduccionPage from "./pages/ReproduccionPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import EmailReportsPage from "./pages/EmailReportsPage";
import PlanesPage from "./pages/PlanesPage";
import ImportarAnimalesPage from "./pages/ImportarAnimalesPage";
import HistorialPage from "./pages/HistorialPage";
import DecisionSupportPage from "./pages/DecisionSupportPage";
import AsistentePage from "./pages/AsistentePage";
import SyncPanelPage from "./pages/SyncPanelPage";
import InvitarClientePage from "./pages/InvitarClientePage";
import TeamPage from "./pages/TeamPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import FrameScorePage from "./pages/FrameScorePage";
import DescartePage from "./pages/DescartePage";
import DashboardOfflinePage from "./pages/DashboardOfflinePage";
import HitosPage from "./pages/HitosPage";
import EventosCampoPage from "./pages/EventosCampoPage";
import ImportarCampoPage from "./pages/ImportarCampoPage";
import InsightsIAPage from "./pages/InsightsIAPage";
import LotesEngordePage from "./pages/LotesEngordePage";
import CostosPage from "./pages/CostosPage";
import ProtocolosPage from "./pages/ProtocolosPage";
import InventarioPage from "./pages/InventarioPage";
import LandingPage from "./pages/LandingPage";
import PreciosPage from "./pages/PreciosPage";
import MainLayout from "./components/Layout/MainLayout";
import OfflineStatusBar from "./components/Offline/OfflineStatusBar";
import { cacheAnimales } from "./services/offlineDB";
import { animalesAPI } from "./services/api";

// Register Service Worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then(
      (reg) => {
        console.log('[SW] Registered:', reg.scope);
        // Buscar nueva version al abrir, al volver a la pestaña y cada 30 min
        reg.update();
        window.addEventListener('focus', () => reg.update());
        setInterval(() => reg.update(), 30 * 60 * 1000);
      },
      (err) => console.log('[SW] Registration failed:', err)
    );
    // Cuando se activa una nueva version, recargar una sola vez para mostrarla
    let recargando = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (recargando) return;
      recargando = true;
      window.location.reload();
    });
  });
}

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  // Pre-cache animals data when user authenticates
  useEffect(() => {
    if (isAuthenticated && user?.establecimiento_id && navigator.onLine) {
      animalesAPI.syncAll(user.establecimiento_id).then(res => {
        const animales = res.data?.data || [];
        if (animales.length > 0) {
          cacheAnimales(animales);
          console.log(`[PWA] Cached ${animales.length} animals for offline use`);
        }
      }).catch(err => console.warn('[PWA] Failed to cache animals:', err.message));
    }
  }, [isAuthenticated, user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return isAuthenticated ? children : <Navigate to="/landing" />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/precios" element={<PreciosPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Routes>
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/animales" element={<AnimalesPage />} />
                    <Route path="/animales/:id" element={<AnimalDetailPage />} />
                    <Route path="/sesiones" element={<SesionesPage />} />
                    <Route path="/sesiones-trabajo" element={<SesionesTrabajoPage />} />
                    <Route path="/proyecciones" element={<ProyeccionesPage />} />
                    <Route path="/alertas" element={<AlertasPage />} />
                    <Route path="/reproduccion" element={<ReproduccionPage />} />
                    <Route path="/analytics" element={<AnalyticsPage />} />
                    <Route path="/email-reports" element={<EmailReportsPage />} />
                    <Route path="/admin/licencias" element={<AdminLicenciasPage />} />
                    <Route path="/planes" element={<PlanesPage />} />
                    <Route path="/importar" element={<ImportarAnimalesPage />} />
                    <Route path="/historial" element={<HistorialPage />} />
                    <Route path="/decisiones" element={<DecisionSupportPage />} />
                    <Route path="/asistente" element={<AsistentePage />} />
                    <Route path="/sincronizacion" element={<SyncPanelPage />} />
                    <Route path="/admin/invitar" element={<InvitarClientePage />} />
                    <Route path="/equipo" element={<TeamPage />} />
                    <Route path="/frame-score" element={<FrameScorePage />} />
                    <Route path="/descarte" element={<DescartePage />} />
                    <Route path="/hitos" element={<HitosPage />} />
                    <Route path="/eventos-campo" element={<EventosCampoPage />} />
                    <Route path="/importar-campo" element={<ImportarCampoPage />} />
                    <Route path="/insights-ia" element={<InsightsIAPage />} />
                    <Route path="/lotes-engorde" element={<LotesEngordePage />} />
                    <Route path="/costos" element={<CostosPage />} />
                    <Route path="/protocolos" element={<ProtocolosPage />} />
                    <Route path="/inventario" element={<InventarioPage />} />
                    <Route path="/offline" element={<DashboardOfflinePage />} />
                    <Route path="/" element={<Navigate to="/dashboard" />} />
                  </Routes>
                  <OfflineStatusBar />
                </MainLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
